import * as yup from 'yup'
import { formatDate } from '@/shared-utils/date-helper'
import { DRIVE, STAY } from '../constants'
import { CAR_FORM_STATE_TYPE } from '../types'

export const CHILD_AGE_REQUIRED = "Please enter children's age"
export const DATE_RANGE_ERROR =
  'Please make sure the drop-off date is the same or later than the pick-up date'
export const DROPOFF_LOCATION_REQUIRED = 'Please select a drop-off location'
export const INVALID_DATE = 'Please select a valid date'
export const INVALID_DATES = 'Please select valid dates'
export const HOTEL_LOCATION_REQUIRED = 'Please enter a valid location'
export const PICKUP_LOCATION_REQUIRED = 'Please select a pick-up location'
export const ROOM_CAPACITY_ERROR =
  'Rooms accommodate up to 4 people. You may need additional rooms for your group'
const TRAVELERS_PER_ROOM = 4

function isChildrenAgeProvided(this: yup.TestContext) {
  const { childrenAges, childrenCount } = this.parent
  return childrenCount === childrenAges.length
}

function isWithinDateRange(this: yup.TestContext) {
  const { endDate, startDate } = this.parent
  const formattedStartDate = formatDate(startDate)
  const formattedEndDate = formatDate(endDate)

  return new Date(formattedStartDate) <= new Date(formattedEndDate)
}

function isWithinRoomCapacity(this: yup.TestContext) {
  const { adultCount, roomCount, childrenCount } = this
    .parent as CAR_FORM_STATE_TYPE
  const travelerCount = adultCount + childrenCount
  const allowedTravelerCount = roomCount * TRAVELERS_PER_ROOM

  return travelerCount <= allowedTravelerCount
}

const searchFormSchema = yup.object().shape({
  childrenAges: yup.array().when(['childrenCount', 'tripType'], {
    is: (childrenCount: number, tripType: string) =>
      tripType.includes(STAY) && childrenCount > 0,
    then: yup.array().test({
      message: CHILD_AGE_REQUIRED,
      name: 'childrenAges',
      test: isChildrenAgeProvided
    }),
    otherwise: yup.array().nullable()
  }),
  endDate: yup.string().when('tripType', {
    is: (tripType: string) => tripType === DRIVE,
    then: yup
      .string()
      .required(INVALID_DATE)
      .when('startDate', {
        is: (startDate: string) => startDate !== '',
        then: yup.string().test({
          message: DATE_RANGE_ERROR,
          name: 'endDate',
          test: isWithinDateRange
        }),
        otherwise: yup.string().nullable()
      }),
    otherwise: yup.string().nullable()
  }),
  endLocation: yup
    .object()
    .nullable()
    .when('oneWay', {
      is: true,
      then: yup.object().nullable().required(DROPOFF_LOCATION_REQUIRED),
      otherwise: yup.object().nullable()
    })
    .when('tripType', {
      is: (tripType: string) => tripType.includes(STAY),
      then: yup.object().nullable().required(HOTEL_LOCATION_REQUIRED),
      otherwise: yup.object().nullable()
    }),
  hotelEndDate: yup.string().when('tripType', {
    is: (tripType: string) => tripType.includes(STAY),
    then: yup.string().required(INVALID_DATES),
    otherwise: yup.string().nullable()
  }),
  hotelStartDate: yup.string().when('tripType', {
    is: (tripType: string) => tripType.includes(STAY),
    then: yup.string().required(INVALID_DATES),
    otherwise: yup.string().nullable()
  }),
  roomCount: yup.number().when('tripType', {
    is: (tripType: string) => tripType.includes(STAY),
    then: yup.number().test({
      message: ROOM_CAPACITY_ERROR,
      name: 'roomCount',
      test: isWithinRoomCapacity
    }),
    otherwise: yup.number().nullable()
  }),
  startDate: yup.string().when('tripType', {
    is: (tripType: string) => tripType === DRIVE,
    then: yup.string().required(INVALID_DATE),
    otherwise: yup.string().nullable()
  }),
  startLocation: yup
    .object()
    .nullable()
    .when('tripType', {
      is: (tripType: string) => tripType === DRIVE,
      then: yup.object().nullable().required(PICKUP_LOCATION_REQUIRED),
      otherwise: yup.object().nullable()
    })
})

export default searchFormSchema
